import Phaser from 'phaser';
import GridBackground from './GridBackground';
import ABCyaPhaser from '@abcya/abcya_phaser';
import SoundAssetKeys from '../SoundAssetKeys';

export default class DrawGrid extends Phaser.GameObjects.Container {
  constructor(scene, squareSize,squaresPerWidth,squaresPerHeight) {
    super(scene);

    this.squareSize = squareSize;
    this.squaresPerWidth = squaresPerWidth;
    this.squaresPerHeight = squaresPerHeight;
    this.gridActions = [];

    this.scratchAction = null;
    this.gridKeys = {};
    this.gridBlocks = [];
    this.colorQueue = [];

    this.realGridContainer = this.scene.add.container();
    this.add(this.realGridContainer);
    this.scratchGridContainer = this.scene.add.graphics();
    this.add(this.scratchGridContainer);

    for (let i = 0; i < this.squaresPerHeight; i++) {
      const row = [];

      for (let j = 0; j < this.squaresPerWidth; j++) {
        const blockObj = {};
        const s = this.scene.add.graphics();

        s.fillStyle('0xffffff');
        s.fillRect(0,0,this.squareSize,this.squareSize);
        s.y = i * this.squareSize;
        s.x = j * this.squareSize;
        s.alpha = 0;
        this.realGridContainer.add(s);
        blockObj.blockShape = s;
        blockObj.colors = [''];
        blockObj.yIndex = i;
        blockObj.xIndex = j;
        row.push(blockObj);
        const key = `block_${i.toString()}_${j.toString()}`;
        this.gridKeys[key] = blockObj;
      }

      this.gridBlocks.push(row);
    }

    this.maskLayer = this.scene.add.graphics();
    this.maskLayer.fillStyle('0x000000');
    this.maskLayer.fillRect(0,0,1,1);

    this.fakeBg = this.scene.add.container();

    const fakeWhite = this.scene.add.rectangle(0,0,this.squareSize * this.squaresPerWidth, this.squareSize * this.squaresPerHeight, '0xFFFFFF').setOrigin(0);

    const fakeGrid = new GridBackground(this.scene, this.squareSize,this.squaresPerWidth,this.squaresPerHeight);
    this.fakeBg.add(fakeWhite,fakeGrid);
    this.fakeBg.grid = fakeGrid;
    this.add(this.fakeBg);

    this.fakeBg.mask = new Phaser.Display.Masks.BitmapMask(this.scene, this.maskLayer);
  }

  drawOnGrid(xPos,yPos,tool,size,color) {
    const startSquare = this.getTargetSquare(xPos,yPos);

    if (!startSquare) return;

    if (tool === 'fill') {
      if (this.isFilling === true) return;
      this.isFilling = true;

      const newList = [startSquare];
      const groupColor = startSquare.colors[startSquare.colors.length - 1];
      this.getContiguous(groupColor,newList,color);
    }
    else {
      const squaresToColor = this.getStrokeSquares(xPos,yPos,size);
      if (!squaresToColor || squaresToColor.length === 0) return;

      // check gap between current square list and last stroke blob, fill in with large enough patch
      let strokeSize = 1;
      switch (size) {
        case 'small':
          strokeSize = 1;
          break;
        case 'medium':
          strokeSize = 2;
          break;
        case 'large':
          strokeSize = 4;
          break;
      }

      const thisBlob = squaresToColor.slice(0);
      if (this.lastStroke && this.lastStroke.length > 0) {

        const blockA = thisBlob[0];
        const blockB = this.lastStroke[0];

        if (!blockB || !blockA) return;
        const dist = this.calculateDistance(blockA.xIndex,blockA.yIndex,blockB.xIndex,blockB.yIndex);

        const cornerXDiff = blockA.xIndex - blockB.xIndex;
        const cornerYDiff = blockA.yIndex - blockB.yIndex;

        // get direction of row of stroke

        let rowYDirection = 0;
        const rowXDirection = Math.abs(cornerXDiff) > Math.abs(cornerYDiff) ? 0 : 1;
        if (rowXDirection === 0) {
          rowYDirection = 1;
        }

        if (dist > 0) {
          const blobsNeeded = Math.ceil(dist) - strokeSize;

          const xChangePerBlock = cornerXDiff / blobsNeeded;
          const yChangePerBlock = cornerYDiff / blobsNeeded;

          let i = 0;
          while (i < blobsNeeded) {
            const yIndex = cornerYDiff < -strokeSize ? blockA.yIndex + strokeSize - Math.floor(i * yChangePerBlock) : blockA.yIndex - Math.floor(i * yChangePerBlock);
            const xIndex = cornerXDiff < -strokeSize ? blockA.xIndex + strokeSize - Math.floor(i * xChangePerBlock) : blockA.xIndex - Math.floor(i * xChangePerBlock);
            if (yIndex > -1 && yIndex < this.squaresPerHeight && xIndex > -1 && xIndex < this.squaresPerWidth) {
              const addBlock = this.gridBlocks[yIndex][xIndex];
              if (addBlock) {
                if (squaresToColor.indexOf(addBlock) === -1) squaresToColor.push(addBlock);

                let r = 0;
                while (r < strokeSize - 1) {
                  const rowBlock = this.gridBlocks[addBlock.yIndex + (rowYDirection * (r + 1))]?.[addBlock.xIndex + (rowXDirection * (r + 1))];
                  if (rowBlock && squaresToColor.indexOf(rowBlock) === -1) squaresToColor.push(rowBlock);

                  r++;
                }
              }
            }

            i++;
          }
        }
      }

      this.lastStroke = thisBlob;
      this.colorQueue = this.colorQueue.concat(squaresToColor);
      this.colorSquares(color, true);
    }
  };

  calculateDistance(pt1x,pt1y,pt2x,pt2y) {
    const xDistance = pt1x - pt2x;
    const yDistance = pt1y - pt2y;
    return Math.sqrt(Math.pow(xDistance, 2) + Math.pow(yDistance, 2));
  }

  colorSquares(newColor,scratch) {
    this.isColoring = true;

    if (!this.curAction) {
      this.curAction = [];
      if (scratch === true) {
        if (!this.scratchAction) this.scratchAction = [];
      }
    }

    while (this.colorQueue.length > 0) {
      const squareToColor = this.colorQueue.shift();

      if (!scratch) {
        if (newColor !== '') {
          squareToColor.blockShape.fillStyle(newColor);
          squareToColor.blockShape.fillRect(0,0,this.squareSize,this.squareSize);
        }
        squareToColor.blockShape.alpha = newColor !== '' ? 1 : 0;
      }
      else {
        if (newColor !== '') {
          this.scratchGridContainer.fillStyle(newColor);
          this.scratchGridContainer.fillRect(squareToColor.xIndex * this.squareSize,squareToColor.yIndex * this.squareSize,this.squareSize,this.squareSize);
        }
        else {
          if (this.fakeBg.alpha !== 1) this.fakeBg.alpha = 1;

          squareToColor.blockShape.alpha = newColor !== '' ? 1 : 0;
        }
      }

      if (this.curAction.indexOf(squareToColor) === -1) {
        squareToColor.colors.push(newColor);
        this.curAction.push(squareToColor);
      }

      if (scratch) this.scratchAction.push(squareToColor);
      if (this.colorQueue.length <= 0 && this.isFilling) this.isFilling = false;
    }
  };

  undoAction() {
    if (this.gridActions.length === 0) return;

    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._pixel_art_select_sound, this.scene);

    const lastAction = this.gridActions.pop();
    while (lastAction.length > 0) {
      // set each square to last color
      const d = lastAction.shift();
      d.colors.pop();
      if (d.colors[d.colors.length - 1] !== '') {
        d.blockShape.fillStyle(d.colors[d.colors.length - 1]);
        d.blockShape.fillRect(0,0,this.squareSize,this.squareSize);
        d.blockShape.alpha = 1;
      }
      else {
        d.blockShape.alpha = 0;
      }
    }
  };

  saveAction() {
    if (this.curAction) {
      if (this.scratchAction !== null) {
        while (this.scratchAction.length > 0) {
          const s = this.scratchAction.shift();
          const transferColor = s.colors[s.colors.length - 1];

          if (transferColor !== '') {
            s.blockShape.fillStyle(transferColor);
            s.blockShape.fillRect(0,0,this.squareSize,this.squareSize);
          }

          s.blockShape.alpha = transferColor === '' ? 0 : 1;
        }

        this.scratchGridContainer.clear();
        this.maskLayer.clear();
        this.fakeBg.alpha = 0;
      }

      this.scratchAction = null;
      this.gridActions.push(this.curAction);
      this.curAction = null;
    }

    this.lastStroke = [];
  };

  getStrokeSquares(xPos,yPos,strokeSize) {
    const startSquare = this.getTargetSquare(xPos,yPos);
    const squareList = [startSquare];
    const startX = startSquare.xIndex;
    const startY = startSquare.yIndex;
    const leftOk = startX > 0;
    const downOk = startY < this.squaresPerHeight - 1;
    const downOk2 = startY < this.squaresPerHeight - 2;
    const rightOk = startX < this.squaresPerWidth - 1;
    const rightOk2 = startX < this.squaresPerWidth - 2;
    const upOk = startY > 0;
    if (strokeSize === 'small') {

    }
    else if (strokeSize === 'medium') {
      // 2x2 block
      if (rightOk) squareList.push(this.gridBlocks[startY][startX + 1]);
      if (downOk) squareList.push(this.gridBlocks[startY + 1][[startX]]);
      if (rightOk && downOk) squareList.push(this.gridBlocks[startY + 1][startX + 1]);
    }
    else {
      // large 4x4 block, start block will be center

      // row above
      if (upOk) {
        if (leftOk) squareList.unshift(this.gridBlocks[startY - 1][startX - 1]);
        squareList.unshift(this.gridBlocks[startY - 1][startX]);
        if (rightOk) squareList.unshift(this.gridBlocks[startY - 1][startX + 1]);
        if (rightOk2) squareList.unshift(this.gridBlocks[startY - 1][startX + 2]);
      }

      if (leftOk) squareList.unshift(this.gridBlocks[startY][startX - 1]);

      if (rightOk) squareList.push(this.gridBlocks[startY][startX + 1]);
      if (rightOk2) squareList.push(this.gridBlocks[startY][startX + 2]);

      if (downOk) {
        if (leftOk) squareList.push(this.gridBlocks[startY + 1][startX - 1]);
        squareList.push(this.gridBlocks[startY + 1][startX]);
        if (rightOk) squareList.push(this.gridBlocks[startY + 1][startX + 1]);
        if (rightOk2) squareList.push(this.gridBlocks[startY + 1][startX + 2]);
      }

      if (downOk2) {
        if (leftOk) squareList.push(this.gridBlocks[startY + 2][startX - 1]);
        squareList.push(this.gridBlocks[startY + 2][startX]);
        if (rightOk) squareList.push(this.gridBlocks[startY + 2][startX + 1]);
        if (rightOk2) squareList.push(this.gridBlocks[startY + 2][startX + 2]);
      }
    }

    return squareList;
  };

  getContiguous(color,checkList,contigColor) {
    const checked = [];
    const squares = [];

    while (checkList.length > 0) {
      const squareToCheck = checkList.shift();

      const squareX = Number(squareToCheck.xIndex);
      const squareY = Number(squareToCheck.yIndex);

      if (squareToCheck.colors[squareToCheck.colors.length - 1] === color) {
        squares.push(squareToCheck);
        if (checked.indexOf(squareToCheck) === - 1) checked.push(squareToCheck);

        if (squareX > 0) {
          if (checked.indexOf(this.gridBlocks[squareY][squareX - 1]) === -1) {
            checkList.push(this.gridBlocks[squareY][squareX - 1]);
            checked.push(this.gridBlocks[squareY][squareX - 1]);
          }
        }

        if (squareX < this.squaresPerWidth - 1) {
          if (checked.indexOf(this.gridBlocks[squareY][squareX + 1]) === -1) {
            checkList.push(this.gridBlocks[squareY][squareX + 1]);
            checked.push(this.gridBlocks[squareY][squareX + 1]);
          }
        }
        if (squareY > 0) {
          if (checked.indexOf(this.gridBlocks[squareY - 1][squareX]) === -1) {
            checkList.push(this.gridBlocks[squareY - 1][squareX]);
            checked.push(this.gridBlocks[squareY - 1][squareX]);
          }
        }

        if (squareY < this.squaresPerHeight - 1) {
          if (checked.indexOf(this.gridBlocks[squareY + 1][squareX]) === -1) {
            checkList.push(this.gridBlocks[squareY + 1][squareX]);
            checked.push(this.gridBlocks[squareY + 1][squareX]);
          }
        }
      }

      if (checkList.length <= 0) {
        this.colorQueue = squares;
        this.colorSquares(contigColor,false);
      }
    }
  };

  getTargetSquare(xPos,yPos) {
    const yNum = Math.floor(yPos / this.squareSize);
    const xNum = Math.floor(xPos / this.squareSize);

    return this.gridKeys[`block_${yNum.toString()}_${xNum.toString()}`];
  };
}